// Decides whether a product is a large furniture item.
//
// Room-fit calculators, the room visualizer and assembly-time copy were built
// for sectionals and beds. On a $79 table lamp they're absurd, so we gate them
// on the product TYPE (never on price).

const LARGE_TYPE_PATTERNS: RegExp[] = [
  // Seating
  /\b(sofa|sofas|sectional|sectionals|loveseat|couch|recliner|chair|chairs|stool|stools|bench|benches|ottoman|ottomans|settee|chaise|futon)\b/i,
  // Tables & desks
  /\b(table|tables|desk|desks|vanity|vanities|island)\b/i,
  // Beds & bedroom
  /\b(bed|beds|bedroom set|headboard|footboard|daybed|bunk|mattress|crib)\b/i,
  // Storage & case goods
  /\b(dresser|dressers|nightstand|nightstands|cabinet|cabinets|bookcase|bookshelf|shelving|etagere|sideboard|buffet|credenza|hutch|armoire|wardrobe|chest|chests|console|cupboard|storage)\b/i,
  // Large outdoor / other
  /\b(patio set|dining set|entertainment center|media center|tv stand|room divider|wardrobe|closet)\b/i,
];

const SMALL_TYPE_PATTERNS: RegExp[] = [
  /\b(lamp|lamps|lampshade|lampshades|light|lights|lighting|sconce|sconces|chandelier|chandeliers|pendant|lantern|lanterns)\b/i,
  /\b(rug|rugs|mat|mats|doormat|doormats|runner)\b/i,
  /\b(pillow|pillows|throw|throws|blanket|blankets|comforter|duvet|quilt|sheet|sheets|bedding|towel|towels|curtain|curtains|drape)\b/i,
  /\b(sculpture|sculptures|vase|vases|figurine|ornament|ornaments|candle|candles|holder|frame|frames|wall art|wall decor|art|print|prints|mirror|mirrors|clock|clocks|planter|basket|baskets|tray|trays|bowl|bookend)\b/i,
  /\b(tile|tiles|wallpaper|decal|hook|hooks|knob|shelf bracket|accessory|accessories)\b/i,
];

/**
 * True for seating, tables, beds, storage and other large furniture.
 * False for lighting, decor, textiles, wall art, mats and other small items.
 */
export function isLargeFurniture(productType?: string | null, title?: string | null): boolean {
  const type = (productType || "").trim();

  if (type) {
    // Small-item vocabulary wins over incidental furniture words in the type
    // ("Table Lamps" is lighting, not a table).
    if (SMALL_TYPE_PATTERNS.some((re) => re.test(type))) return false;
    if (LARGE_TYPE_PATTERNS.some((re) => re.test(type))) return true;
    return false;
  }

  // No product type from the feed — fall back to the title, letting whichever
  // vocabulary appears LAST decide (English puts the head noun last).
  const t = (title || "").trim();
  if (!t) return false;
  const latest = (patterns: RegExp[]) =>
    patterns.reduce((best, re) => {
      const m = t.match(new RegExp(re.source, "gi"));
      if (!m) return best;
      const idx = t.toLowerCase().lastIndexOf(m[m.length - 1].toLowerCase());
      return Math.max(best, idx);
    }, -1);
  const small = latest(SMALL_TYPE_PATTERNS);
  const large = latest(LARGE_TYPE_PATTERNS);
  if (small < 0 && large < 0) return false;
  return large > small;
}

